import clsx from '../utils/clsx';
import { useMemo } from 'react';
import { thumbRects } from '../engine/scenes/thumb';
import styles from '../styles/Battlefield.module.css';
import BattlefieldFileButton from './BattlefieldFileButton';

/** "off stage 12 min" - coarse on purpose; a card is a glance, not a clock. */
export function offStageFor(lastOnStageAt, now) {
  if (lastOnStageAt === null || lastOnStageAt === undefined) return 'never on stage';
  const min = Math.max(0, Math.round((now - lastOnStageAt) / 60000));
  if (min < 1) return 'off stage just now';
  if (min < 60) return `off stage ${min} min`;
  return `off stage ${Math.round(min / 60)} h`;
}

/** Whose turn a parked order is on, or nothing when it is not running. */
const turnOf = (tracker) =>
  tracker?.active && tracker.participants.length ? (tracker.participants[tracker.activeIndex]?.name ?? null) : null;

/**
 * What a card says: the ON STAGE card reads the live table
 * (`stageSummary`), every other card its parked record.
 */
function cardFacts(scene, on, stageSummary) {
  if (!on) return { count: scene.castIds.length, round: scene.tracker.round, turn: turnOf(scene.tracker) };
  return {
    count: stageSummary?.count ?? scene.castIds.length,
    round: stageSummary?.round ?? scene.tracker.round,
    turn: stageSummary?.turn ?? null,
  };
}

/** One of a card's small verb buttons. */
function Verb({ testId, disabled, title, label, onClick, className = styles.sceneVerb, children }) {
  return (
    <button
      type="button"
      className={className}
      data-testid={testId}
      disabled={disabled}
      title={title}
      aria-label={label}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** The GM's library on a card (v11.77.0-v11.78.0): Update when the library has moved past a copy; Save to it. */
function LibraryVerbs({ scene, on, busy, verbs }) {
  const id = scene.id;
  const newer = on ? null : verbs.newerOf?.(scene);
  return (
    <>
      {newer && (
        <Verb
          testId={`scene-library-update-${id}`}
          disabled={busy}
          title={`Newer in your library: update to v${newer}`}
          label={`Update ${scene.name} to v${newer} from your library`}
          onClick={() => verbs.onUpdateFromLibrary?.(id)}
        >
          → v{newer}
        </Verb>
      )}
      {verbs.onSaveToLibrary && (
        <Verb
          testId={`scene-save-${id}`}
          disabled={busy}
          title="Save to my library - a new version, wounds and all"
          label={`Save ${scene.name} to my library`}
          onClick={() => verbs.onSaveToLibrary(id)}
        >
          ⇪
        </Verb>
      )}
    </>
  );
}

/**
 * The card's verbs. Cut, reset and delete are for the wings; the scene on stage is the table. A copy of a library
 * scene the library has moved past offers its Update (`newerOf` gives that version; v11.77.0); with a library, any
 * scene - the one on stage too - can be saved to it (v11.78.0).
 */
function SceneVerbs({ scene, on, busy, verbs }) {
  const id = scene.id;
  return (
    <div className={styles.sceneVerbs}>
      {!on && (
        <Verb
          className={styles.roomDeploy}
          testId={`scene-cut-${id}`}
          disabled={busy}
          title={`Cut to ${scene.name}`}
          onClick={() => verbs.onCutTo?.(id)}
        >
          ▶ Cut to
        </Verb>
      )}
      <Verb testId={`scene-rename-${id}`} title="Rename" onClick={() => verbs.onRename?.(id)}>
        ✎
      </Verb>
      <Verb testId={`scene-duplicate-${id}`} disabled={busy} title="Duplicate" onClick={() => verbs.onDuplicate?.(id)}>
        ⧉
      </Verb>
      <Verb testId={`scene-export-${id}`} disabled={busy} title="Export to a file" onClick={() => verbs.onExport?.(id)}>
        ⇩
      </Verb>
      <LibraryVerbs scene={scene} on={on} busy={busy} verbs={verbs} />
      {!on && (
        <>
          <Verb
            testId={`scene-reset-${id}`}
            disabled={busy}
            title="Reset - full wounds, empty order, positions kept"
            onClick={() => verbs.onReset?.(id)}
          >
            ↺
          </Verb>
          <Verb
            testId={`scene-delete-${id}`}
            disabled={busy}
            title="Delete"
            label={`Delete ${scene.name}`}
            onClick={() => verbs.onDelete?.(id)}
          >
            ✕
          </Verb>
        </>
      )}
    </div>
  );
}

/** The thumbnail's box, in its own units. */
const THUMB_W = 72;
const THUMB_H = 48;

/** The set seen from above, as outlines (engine/scenes/thumb): which room this is, at a glance. */
function SceneThumb({ set }) {
  const rects = useMemo(() => (set ? thumbRects(set.solids, THUMB_W, THUMB_H, set.extent) : null), [set]);
  if (!rects) return null;
  return (
    <svg
      className={styles.sceneThumb}
      viewBox={`0 0 ${THUMB_W} ${THUMB_H}`}
      aria-hidden="true"
      data-testid="scene-thumb"
    >
      {rects.map((r, i) => (
        <rect key={i} x={r.x} y={r.y} width={r.w} height={r.h} />
      ))}
    </svg>
  );
}

function SceneCard({ scene, on, rail }) {
  const { count, round, turn } = cardFacts(scene, on, rail.stageSummary);
  return (
    <div
      role="listitem"
      className={clsx(styles.sceneCard, on && styles.sceneCardOn)}
      data-testid={`scene-card-${scene.id}`}
    >
      <SceneThumb set={rail.thumbOf?.(scene.id) ?? null} />
      <div className={styles.sceneCardHead}>
        <span className={styles.sceneName} data-testid={`scene-name-${scene.id}`}>
          {on ? rail.stageName || scene.name : scene.name}
        </span>
        {on && (
          <span className={styles.sceneOnStage} data-testid="scene-on-stage">
            ON STAGE
          </span>
        )}
      </div>
      <div className={styles.sceneMeta}>
        <span data-testid={`scene-count-${scene.id}`} title="In the cast">
          {count} {count === 1 ? 'person' : 'people'}
        </span>
        <span data-testid={`scene-round-${scene.id}`} title="Round">
          R{round}
        </span>
        {scene.mapName && <span title="Map">{scene.mapName}</span>}
      </div>
      <div className={styles.sceneMeta}>
        {turn && <span title="Whose turn">{`${turn}'s turn`}</span>}
        {!on && <span>{offStageFor(scene.lastOnStageAt, rail.now)}</span>}
      </div>
      <SceneVerbs scene={scene} on={on} busy={rail.busy} verbs={rail} />
    </div>
  );
}

/** Pick a scene file to import. */
function ImportButton({ onImport }) {
  return (
    <BattlefieldFileButton
      className={styles.roomNew}
      testId="scene-import"
      accept=".json,application/json"
      inputTestId="scene-import-file"
      onFile={onImport}
    >
      Import…
    </BattlefieldFileButton>
  );
}

/** The rail's own verbs: a new scene, strike the stage, the curtain, cut back, import, and the GM's library. */
function SceneRailHead({ rail, unnamedStage }) {
  const { busy, curtain } = rail;
  return (
    <div className={styles.sceneHead}>
      <Verb className={styles.roomNew} testId="scene-new" disabled={busy} onClick={rail.onNew}>
        + New scene
      </Verb>
      <Verb
        className={styles.roomNew}
        testId="scene-strike"
        disabled={busy}
        title={unnamedStage ? 'Name the stage as a scene' : 'Rename the scene on stage'}
        onClick={rail.onStrike}
      >
        {unnamedStage ? 'Strike' : 'Rename stage'}
      </Verb>
      <button
        type="button"
        className={clsx(styles.roomNew, curtain && styles.sceneCurtainDown)}
        data-testid="scene-curtain"
        aria-pressed={curtain}
        title={curtain ? 'Raise the curtain - players see the map' : 'Lower the curtain - players see the title card'}
        onClick={() => rail.onCurtain?.(!curtain)}
      >
        {curtain ? 'Curtain ↑' : 'Curtain ↓'}
      </button>
      <Verb
        className={styles.roomNew}
        testId="scene-cut-back"
        disabled={busy || !rail.canCutBack}
        title="Back to the previous scene"
        onClick={rail.onCutBack}
      >
        ↶ Cut back
      </Verb>
      <ImportButton onImport={rail.onImport} />
      {rail.onFromLibrary && (
        <Verb className={styles.roomNew} testId="library-pick-scene" disabled={busy} onClick={rail.onFromLibrary}>
          From my library…
        </Verb>
      )}
    </div>
  );
}

/**
 * The scenes, down the side of Scenes mode - the wings, and the one on
 * stage.
 *
 * The same place the waiting rooms take in Deploy, for the same reason:
 * it belongs to the mode rather than to the map. One card per scene;
 * the ON STAGE card reads the live table (`stageSummary`), every other
 * card its parked record. The verbs are the hook's; this decides
 * nothing (v8.338.0).
 */
export default function BattlefieldSceneRail(props) {
  const { scenes = [], onStageId = null, status = null } = props;
  const rail = {
    ...props,
    stageSummary: props.stageSummary ?? null,
    stageName: props.stageName ?? '',
    curtain: props.curtain ?? false,
    canCutBack: props.canCutBack ?? false,
    busy: props.busy ?? false,
    now: props.now ?? Date.now(),
  };
  const unnamedStage = !onStageId;
  return (
    <div className={clsx(styles.roomRail, styles.sceneRail)} data-testid="battlefield-scenes">
      <div className={styles.roomRailHead}>Scenes</div>
      <SceneRailHead rail={rail} unnamedStage={unnamedStage} />
      {status && (
        <div className={styles.sceneStatus} role="status" data-testid="scene-status">
          {status}
        </div>
      )}
      {unnamedStage && (
        <div className={styles.roomEmpty} data-testid="scene-unnamed">
          The stage is unnamed - Strike to keep it.
        </div>
      )}
      <div className={styles.roomList} role="list" aria-label="Scenes">
        {scenes.length === 0 && !unnamedStage && <div className={styles.roomEmpty}>No scenes in the wings</div>}
        {scenes.map((scene) => (
          <SceneCard key={scene.id} scene={scene} on={scene.id === onStageId} rail={rail} />
        ))}
      </div>
    </div>
  );
}
